import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import "./GameMediaPreview.scss";
import ImageFallbackGamePoster from "../../../images/fallback-game-poster.png";
import PlayIcon from "../../icons/PlayIcon";
import SpeakerOffIcon from "../../icons/SpeakerOffIcon";
import SpeakerOnIcon from "../../icons/SpeakerOnIcon";

interface GameMediaPreviewProps {
  gameName?: string;
  poster?: string;
  mediaUrl?: string;
  frameClassName?: string;
}

export default function GameMediaPreview({
  gameName,
  poster,
  mediaUrl,
  frameClassName = "",
}: GameMediaPreviewProps) {
  const { t } = useTranslation("games");
  const posterUrl = poster || ImageFallbackGamePoster;
  const [hasVideoError, setHasVideoError] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);

  const handleLoadedData = () => {
    const video = videoRef.current;
    if (!video) return;
    video.play().catch(() => setIsPlaying(false));
  };

  const handlePlayPause = () => {
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      video.play().catch(() => setIsPlaying(false));
    } else {
      video.pause();
    }
  };

  const handleToggleMute = () => {
    const video = videoRef.current;
    if (!video) return;

    const nextMuted = !video.muted;
    video.muted = nextMuted;
    setIsMuted(nextMuted);
  };

  return (
    <div className={`GameMediaPreview ${frameClassName}`}>
      <img
        src={posterUrl}
        alt={gameName ?? t("practice_game")}
        className="GameMediaPreview__poster"
        onError={(event) => {
          event.currentTarget.onerror = null;
          event.currentTarget.src = ImageFallbackGamePoster;
        }}
      />
      {mediaUrl && !hasVideoError ? (
        <>
          <video
            ref={videoRef}
            autoPlay
            loop
            muted={isMuted}
            playsInline
            preload="metadata"
            src={mediaUrl}
            className="GameMediaPreview__video"
            onClick={handlePlayPause}
            onLoadedData={handleLoadedData}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onError={() => setHasVideoError(true)}
          >
            {t("sorry_your_browser_does_not_support_video_content")}
          </video>

          {!isPlaying ? (
            <button
              type="button"
              className="GameMediaPreview__playOverlay"
              onClick={handlePlayPause}
              aria-label={t("play")}
            >
              <span className="GameMediaPreview__playIconContainer">
                <PlayIcon />
              </span>
            </button>
          ) : null}

          <button
            type="button"
            className="GameMediaPreview__volumeButton"
            onClick={handleToggleMute}
            aria-label={isMuted ? t("unmute") : t("mute")}
          >
            {isMuted ? <SpeakerOffIcon /> : <SpeakerOnIcon />}
          </button>
        </>
      ) : null}
    </div>
  );
}
